// AgentTagChip — removable pill showing the bot @-tagged in (or launched
// into) the composer. Tagging no longer lives inline in the text: the
// composer keeps plain `@Name` text for message context, while this chip
// owns visibility and removal (agentIds carry the actual targeting).

import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Image } from 'expo-image';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { AGENT_ICON } from '@/constants/config';

export default function AgentTagChip({ agent, onRemove }) {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const styles = React.useMemo(() => createStyles(colors, isXS), [colors, isXS]);

  if (!agent?.id) return null;

  return (
    <View
      style={styles.chip}
      accessibilityLabel={`Bot ${agent.name} tagged`}
      testID="agent-tag-chip"
    >
      {agent.avatarPath ? (
        <Image source={{ uri: agent.avatarPath }} style={styles.avatar} contentFit="cover" />
      ) : (
        <View style={styles.avatar}>
          <Icon name={AGENT_ICON} size={9} color={colors.text_color_secondary} />
        </View>
      )}
      <Text size="xs" weight="medium" numberOfLines={1} style={styles.name}>
        {agent.name}
      </Text>
      <Pressable
        onPress={onRemove}
        accessibilityRole="button"
        accessibilityLabel={`Remove ${agent.name}`}
        hitSlop={6}
        style={({ hovered }) => [styles.remove, hovered && { backgroundColor: colors.solid_color_secondary }]}
      >
        <Icon name="xmark" size={10} color={colors.text_color_secondary} />
      </Pressable>
    </View>
  );
}

const createStyles = (colors, isXS) => StyleSheet.create({
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    alignSelf: 'flex-start',
    maxWidth: 260,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    backgroundColor: colors.solid_color_tertiary,
    borderRadius: 999,
    paddingVertical: 3,
    paddingLeft: 4,
    paddingRight: 3,
  },
  avatar: {
    width: 18,
    height: 18,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.solid_color_secondary,
  },
  name: {
    flexShrink: 1,
    lineHeight: isXS ? 16 : 17,
  },
  remove: {
    width: 18,
    height: 18,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
